import React, { useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  safetyWindows,
  normalizeSafetyRule,
  safetyCountAt,
  safetyCountWord,
  safetyNeededWhen,
  safetyRuleSummary,
  safetyCrewValues,
  SAFETY_RULE_EVERY,
  SAFETY_RULE_FIRST_THEN,
  SAFETY_RULE_THREE
} from '../../utils/staffingPlan';
import { exportFilename } from '../../utils/nomenclature';
import { applyLetterhead } from '../../utils/pdfLetterhead';
import { addExtrasWorksheet, addHorizontalPdfTables, addHorizontalWorksheet, downloadBlob, styleHeaderRow } from '../../utils/planExport';
import { extrasTableRows, loadExtrasCfg, projectMonthKeys } from '../../utils/gcIndirectExtras';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatWeek(ymd) {
  if (!ymd) return { tip: '' };
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  return { tip: `${d} ${MONTHS[(m || 1) - 1]} ${y}` };
}

function formatCrew(n) {
  const r = Math.round((Number(n) || 0) * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

function safetyNoteLines({ jobNumber, sourceNote, safetyRule, safetyPeak }) {
  return [
    `Job ${jobNumber || ''}`,
    `Rule: ${safetyRuleSummary(safetyRule)}`,
    `Peak crew ${formatCrew(safetyPeak)} men → ${safetyCountWord(safetyCountAt(safetyPeak, safetyRule))} at peak.`,
    sourceNote,
    'Weeks = Sunday weeks the crew is over that line. From / To is the first and last of those weeks.',
    `Generated ${new Date().toLocaleString()}.`
  ];
}

function safetyByWeekRows(safetyPts, safetyRule) {
  const counts = (safetyPts || []).map((p) => safetyCountAt(p.men, safetyRule));
  const peak = counts.reduce((m, n) => Math.max(m, n), 0);
  const rows = [];
  for (let n = 1; n <= peak; n += 1) {
    rows.push({
      label: `Safety #${n}`,
      values: counts.map((c) => (c >= n ? 1 : ''))
    });
  }
  return rows;
}

async function exportSafetyXlsx({
  jobNumber, safetyRows, safetyRule, safetyPeak, sourceNote, safetyPts = []
}) {
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'LGL Portal';
  const ws = wb.addWorksheet('Safety plan');
  ws.columns = [
    { header: 'Safety', key: 'safety', width: 14 },
    { header: 'Needed when', key: 'when', width: 28 },
    { header: 'From', key: 'from', width: 16 },
    { header: 'To', key: 'to', width: 16 },
    { header: 'Weeks', key: 'weeks', width: 10 },
    { header: 'Peak crew', key: 'peak', width: 12 }
  ];
  safetyRows.forEach((r) => {
    ws.addRow({
      safety: `Safety #${r.n}`,
      when: safetyNeededWhen(r.threshold, safetyRule),
      from: formatWeek(r.from).tip,
      to: formatWeek(r.to).tip,
      weeks: r.weeks,
      peak: formatCrew(r.peak)
    });
  });
  styleHeaderRow(ws, 'FFB91C1C');
  addHorizontalWorksheet(wb, {
    name: 'By week',
    rowHeader: 'Safety',
    columns: safetyPts.map((p) => formatWeek(p.date).tip),
    rows: safetyByWeekRows(safetyPts, safetyRule),
    argb: 'FFB91C1C'
  });
  const notes = wb.addWorksheet('Notes');
  notes.getColumn(1).width = 96;
  safetyNoteLines({ jobNumber, sourceNote, safetyRule, safetyPeak }).forEach((line, i) => {
    notes.getCell(i + 1, 1).value = line;
  });
  const months = projectMonthKeys(safetyPts.map((p) => p.date));
  addExtrasWorksheet(wb, {
    jobNumber,
    cfg: loadExtrasCfg(jobNumber),
    projectMonths: months.length,
    months
  });
  const buf = await wb.xlsx.writeBuffer();
  downloadBlob(
    exportFilename({ job: jobNumber, reportSlug: 'safety-plan', ext: 'xlsx' }),
    new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  );
}

async function buildSafetyPdf({
  jobNumber, instance, accounts, safetyRows, safetyRule, safetyPeak, sourceNote, safetyPts = []
}) {
  const [{ default: jsPDF }, autoTableMod] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable')
  ]);
  const autoTable = autoTableMod.default;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const { y: letterheadY } = await applyLetterhead(doc, { instance, accounts, jobNumber, margin: 14 });
  doc.setFontSize(14);
  doc.setTextColor(15, 23, 42);
  doc.text('Safety staffing plan', 14, letterheadY);
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  const ruleLine = `${safetyRuleSummary(safetyRule)} Peak ${formatCrew(safetyPeak)} men.`;
  const ruleLines = doc.splitTextToSize(ruleLine, 180);
  doc.text(ruleLines, 14, letterheadY + 5);
  const ruleBlockH = Math.max(5, ruleLines.length * 4);
  autoTable(doc, {
    startY: letterheadY + 4 + ruleBlockH,
    head: [['Safety', 'Needed when', 'From', 'To', 'Weeks', 'Peak crew']],
    body: safetyRows.map((r) => [
      `Safety #${r.n}`,
      safetyNeededWhen(r.threshold, safetyRule),
      formatWeek(r.from).tip,
      formatWeek(r.to).tip,
      r.weeks,
      formatCrew(r.peak)
    ]),
    styles: { fontSize: 8, cellPadding: 1.6 },
    headStyles: { fillColor: [185, 28, 28], fontSize: 8 },
    alternateRowStyles: { fillColor: [254, 242, 242] }
  });
  const months = projectMonthKeys(safetyPts.map((p) => p.date));
  const extras = extrasTableRows(loadExtrasCfg(jobNumber), months.length).map((r) => [r[0], r[2], r[4], r[5]]);
  if (extras.length) {
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 6,
      head: [['Code', 'Cost item', 'Qty', 'Months']],
      body: extras,
      styles: { fontSize: 7.5, cellPadding: 1.3 },
      headStyles: { fillColor: [124, 58, 237], fontSize: 8 },
      alternateRowStyles: { fillColor: [245, 243, 255] }
    });
  }
  const weekRows = safetyByWeekRows(safetyPts, safetyRule);
  if (weekRows.length) {
    doc.addPage('letter', 'landscape');
    addHorizontalPdfTables(doc, autoTable, {
      title: 'Safety by week',
      columns: safetyPts.map((p) => formatWeek(p.date).tip),
      rows: weekRows,
      startY: 16,
      headColor: [185, 28, 28]
    });
  }
  doc.setFontSize(7.5);
  doc.setTextColor(100, 116, 139);
  let y = doc.lastAutoTable.finalY + 6;
  const pageH = doc.internal.pageSize.getHeight();
  safetyNoteLines({ jobNumber, sourceNote, safetyRule, safetyPeak }).forEach((line) => {
    if (y > pageH - 12) { doc.addPage(); y = 14; }
    doc.text(line, 14, y, { maxWidth: doc.internal.pageSize.getWidth() - 28 });
    y += 4.5;
  });
  return doc;
}

/**
 * Safety #1 / #2… table. Crew is passed in — Histogram remaining bars, or the
 * Staffing curve — so the popup can stay on whichever tab the user is on.
 */
export default function SafetyPlanModal({
  open,
  onClose,
  crew = [],
  safetyRule: safetyRuleProp,
  onSafetyRule,
  source = 'histogram',
  cfg,
  onCfg,
  jobNumber
}) {
  const { instance, accounts } = useMsal();
  const [exporting, setExporting] = useState(null);
  const safetyRule = useMemo(() => normalizeSafetyRule(safetyRuleProp), [safetyRuleProp]);
  // Histogram bars include purple. The table you can check against those
  // bars always uses the full remaining stack. Exclude is a direct-only note.
  const barCfg = useMemo(
    () => (source === 'histogram' ? { ...(cfg || {}), includeIndirect: true } : (cfg || {})),
    [source, cfg]
  );
  const safetyPts = useMemo(
    () => safetyCrewValues(crew, barCfg),
    [crew, barCfg]
  );
  const directPts = useMemo(
    () => safetyCrewValues(crew, { ...(cfg || {}), includeIndirect: false }),
    [crew, cfg]
  );
  const safetyRows = useMemo(
    () => safetyWindows(safetyPts.map((p) => p.date), safetyPts.map((p) => p.men), safetyRule),
    [safetyPts, safetyRule]
  );
  const safetyPeak = useMemo(
    () => safetyPts.reduce((m, p) => Math.max(m, p.men), 0),
    [safetyPts]
  );
  const directRows = useMemo(
    () => safetyWindows(directPts.map((p) => p.date), directPts.map((p) => p.men), safetyRule),
    [directPts, safetyRule]
  );
  const directPeak = useMemo(
    () => directPts.reduce((m, p) => Math.max(m, p.men), 0),
    [directPts]
  );
  const showDirectNote = source === 'histogram' && cfg?.includeIndirect === false;

  if (!open) return null;

  const patchCfg = (patch) => onCfg?.({ ...(cfg || {}), ...patch });
  const sourceNote = source === 'staffing'
    ? 'Crew = this tab’s curve (earned to the data date, then the forecast, with your hiring overrides).'
    : 'Crew = remaining man-weeks on the histogram (the stacked bars you can count). Hiring overrides on Staffing are not used.';

  const onExportXlsx = async () => {
    setExporting('xlsx');
    try {
      await exportSafetyXlsx({ jobNumber, safetyRows, safetyRule, safetyPeak, sourceNote, safetyPts });
    } catch (err) {
      console.error('Safety xlsx export failed:', err);
      alert(`xlsx export failed: ${err.message || err}`);
    } finally {
      setExporting(null);
    }
  };

  const onExportPdf = async () => {
    setExporting('pdf');
    try {
      const doc = await buildSafetyPdf({
        jobNumber, instance, accounts, safetyRows, safetyRule, safetyPeak, sourceNote, safetyPts
      });
      doc.save(exportFilename({ job: jobNumber, reportSlug: 'safety-plan', ext: 'pdf' }));
    } catch (err) {
      console.error('Safety PDF export failed:', err);
      alert(`PDF export failed: ${err.message || err}`);
    } finally {
      setExporting(null);
    }
  };

  const exportBtn = (label, onClick, { solid = false } = {}) => (
    <button
      type="button"
      onClick={onClick}
      disabled={!!exporting}
      style={{
        border: '1px solid #0f766e', background: solid ? '#0f766e' : 'white',
        color: solid ? 'white' : '#0f766e',
        borderRadius: 7, fontSize: '0.75rem', fontWeight: 800, padding: '0.35rem 0.7rem',
        cursor: exporting ? 'wait' : 'pointer', whiteSpace: 'nowrap'
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)', zIndex: 1100,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Safety staffing plan"
        style={{
          background: 'white', borderRadius: 12, width: 'min(760px, 96vw)', maxHeight: '86vh',
          overflow: 'auto', boxShadow: '0 24px 60px rgba(15, 23, 42, 0.35)'
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, padding: '0.85rem 1rem',
          borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, background: 'white', zIndex: 1
        }}>
          <div>
            <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '1rem' }}>Safety staffing plan</div>
            <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 2 }}>
              {safetyRuleSummary(safetyRule)}{' '}{sourceNote}
            </div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
            {exportBtn(exporting === 'xlsx' ? 'Exporting…' : '⬇ xlsx', onExportXlsx, { solid: true })}
            {exportBtn(exporting === 'pdf' ? 'Exporting…' : '⬇ PDF', onExportPdf)}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ border: 'none', background: 'transparent', fontSize: 20, color: '#64748b', cursor: 'pointer', lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        <div style={{
          padding: '0.7rem 1rem', display: 'flex', flexWrap: 'wrap', gap: '0.55rem 1.1rem',
          alignItems: 'center', borderBottom: '1px solid #f1f5f9'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.75rem', color: '#334155' }} role="radiogroup" aria-label="Safety rule">
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: safetyRule.mode === SAFETY_RULE_EVERY ? 700 : 500 }}>
              <input
                type="radio"
                name="safety-rule-plan"
                checked={safetyRule.mode === SAFETY_RULE_EVERY}
                onChange={() => onSafetyRule?.({ mode: SAFETY_RULE_EVERY })}
                style={{ accentColor: '#dc2626' }}
              />
              1 per
              <input
                type="number"
                min={1}
                value={safetyRule.menPer}
                disabled={safetyRule.mode !== SAFETY_RULE_EVERY}
                onChange={(e) => onSafetyRule?.({ menPer: e.target.value })}
                style={{ width: 54, fontSize: '0.78rem', border: '1px solid #cbd5e1', borderRadius: 5, padding: '3px 5px' }}
              />
              men
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontWeight: safetyRule.mode === SAFETY_RULE_FIRST_THEN ? 700 : 500 }}>
              <input
                type="radio"
                name="safety-rule-plan"
                checked={safetyRule.mode === SAFETY_RULE_FIRST_THEN}
                onChange={() => onSafetyRule?.({ mode: SAFETY_RULE_FIRST_THEN })}
                style={{ accentColor: '#dc2626' }}
              />
              First at
              <input
                type="number"
                min={1}
                value={safetyRule.firstAt}
                disabled={safetyRule.mode !== SAFETY_RULE_FIRST_THEN}
                onChange={(e) => onSafetyRule?.({ firstAt: e.target.value })}
                style={{ width: 54, fontSize: '0.78rem', border: '1px solid #cbd5e1', borderRadius: 5, padding: '3px 5px' }}
              />
              then every
              <input
                type="number"
                min={1}
                value={safetyRule.thenEvery}
                disabled={safetyRule.mode !== SAFETY_RULE_FIRST_THEN}
                onChange={(e) => onSafetyRule?.({ thenEvery: e.target.value })}
                style={{ width: 54, fontSize: '0.78rem', border: '1px solid #cbd5e1', borderRadius: 5, padding: '3px 5px' }}
              />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontWeight: safetyRule.mode === SAFETY_RULE_THREE ? 700 : 500 }}>
              <input
                type="radio"
                name="safety-rule-plan"
                checked={safetyRule.mode === SAFETY_RULE_THREE}
                onChange={() => onSafetyRule?.({ mode: SAFETY_RULE_THREE })}
                style={{ accentColor: '#dc2626' }}
              />
              First at
              <input
                type="number"
                min={1}
                value={safetyRule.firstAt}
                disabled={safetyRule.mode !== SAFETY_RULE_THREE}
                onChange={(e) => onSafetyRule?.({ firstAt: e.target.value })}
                style={{ width: 54, fontSize: '0.78rem', border: '1px solid #cbd5e1', borderRadius: 5, padding: '3px 5px' }}
              />
              next +
              <input
                type="number"
                min={1}
                value={safetyRule.thenEvery}
                disabled={safetyRule.mode !== SAFETY_RULE_THREE}
                onChange={(e) => onSafetyRule?.({ thenEvery: e.target.value })}
                style={{ width: 54, fontSize: '0.78rem', border: '1px solid #cbd5e1', borderRadius: 5, padding: '3px 5px' }}
              />
              then every
              <input
                type="number"
                min={1}
                value={safetyRule.finalEvery}
                disabled={safetyRule.mode !== SAFETY_RULE_THREE}
                onChange={(e) => onSafetyRule?.({ finalEvery: e.target.value })}
                style={{ width: 54, fontSize: '0.78rem', border: '1px solid #cbd5e1', borderRadius: 5, padding: '3px 5px' }}
              />
            </label>
            {safetyRule.mode === SAFETY_RULE_THREE && (
              <div style={{ fontSize: '0.7rem', color: '#64748b', marginLeft: 22 }}>
                {safetyRule.firstAt} men → 1 safety, {safetyRule.firstAt + safetyRule.thenEvery} → 2, then one more every {safetyRule.finalEvery}
              </div>
            )}
          </div>
          <div style={{ display: 'inline-flex', border: '1px solid #cbd5e1', borderRadius: 8, overflow: 'hidden' }} role="group" aria-label="Indirect handling">
            {[[true, 'Include indirect'], [false, 'Exclude indirect']].map(([val, lbl]) => (
              <button
                key={lbl}
                type="button"
                onClick={() => patchCfg({ includeIndirect: val })}
                style={{
                  border: 'none', padding: '0.28rem 0.6rem', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
                  background: cfg?.includeIndirect === val ? '#0f766e' : '#f8fafc',
                  color: cfg?.includeIndirect === val ? 'white' : '#334155'
                }}
              >
                {lbl}
              </button>
            ))}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.75rem', color: '#334155' }}>
            From
            <input
              type="date"
              value={cfg?.from || ''}
              onChange={(e) => patchCfg({ from: e.target.value })}
              style={{ fontSize: '0.72rem', border: '1px solid #cbd5e1', borderRadius: 5, padding: '2px 5px' }}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.75rem', color: '#334155' }}>
            To
            <input
              type="date"
              value={cfg?.to || ''}
              onChange={(e) => patchCfg({ to: e.target.value })}
              style={{ fontSize: '0.72rem', border: '1px solid #cbd5e1', borderRadius: 5, padding: '2px 5px' }}
            />
          </label>
          {(cfg?.from || cfg?.to) && (
            <button
              type="button"
              onClick={() => patchCfg({ from: '', to: '' })}
              style={{ border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 5, fontSize: '0.7rem', padding: '0.2rem 0.5rem', cursor: 'pointer', color: '#334155' }}
            >
              Whole schedule
            </button>
          )}
        </div>

        <div style={{ padding: '0.8rem 1rem 1rem' }}>
          {safetyRows.length === 0 ? (
            <div style={{ fontSize: '0.82rem', color: '#64748b', padding: '1rem 0' }}>
              {safetyPeak > 0 && safetyRule.mode !== SAFETY_RULE_EVERY && safetyPeak < safetyRule.firstAt
                ? `Peak crew ${formatCrew(safetyPeak)} men is below ${safetyRule.firstAt} — no safety on this rule.`
                : 'No crew in this window — clear the dates or widen the filters.'}
            </div>
          ) : (
            <>
              <div style={{ fontSize: '0.8rem', color: '#0f172a', fontWeight: 700, marginBottom: 8 }}>
                Peak crew {formatCrew(safetyPeak)} men → {safetyCountWord(safetyCountAt(safetyPeak, safetyRule))} at peak
                {source === 'staffing' && cfg?.includeIndirect === false ? ' (indirect excluded)' : ''}
              </div>
              {showDirectNote && (
                <div style={{ fontSize: '0.72rem', color: '#64748b', margin: '-4px 0 8px', lineHeight: 1.4 }}>
                  Table matches the stacked bars (indirect included). Direct only would be peak{' '}
                  {formatCrew(directPeak)} men
                  {directRows[1] ? `, Safety #2 ${directRows[1].weeks} weeks` : ''}.
                </div>
              )}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                <thead>
                  <tr>
                    {['Safety', 'Needed when', 'From', 'To', 'Weeks', 'Peak crew'].map((h) => (
                      <th key={h} style={{
                        textAlign: h === 'Safety' || h === 'Needed when' ? 'left' : 'right',
                        padding: '0.3rem 0.45rem', borderBottom: '2px solid #e2e8f0',
                        fontSize: '0.66rem', color: '#64748b', letterSpacing: '0.03em', textTransform: 'uppercase'
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {safetyRows.map((r) => (
                    <tr key={r.n} style={{ background: r.n % 2 === 0 ? '#f8fafc' : 'white' }}>
                      <td style={{ padding: '0.35rem 0.45rem', fontWeight: 800, color: '#b91c1c', whiteSpace: 'nowrap' }}>Safety #{r.n}</td>
                      <td style={{ padding: '0.35rem 0.45rem', color: '#334155' }}>
                        {safetyNeededWhen(r.threshold, safetyRule)}
                      </td>
                      <td style={{ padding: '0.35rem 0.45rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatWeek(r.from).tip}</td>
                      <td style={{ padding: '0.35rem 0.45rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatWeek(r.to).tip}</td>
                      <td style={{ padding: '0.35rem 0.45rem', textAlign: 'right' }}>{r.weeks}</td>
                      <td style={{ padding: '0.35rem 0.45rem', textAlign: 'right' }}>{formatCrew(r.peak)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: 8, lineHeight: 1.4 }}>
                Weeks = how many Sunday weeks the crew is over that line (the bars you can count).
                From / To is the first and last of those weeks — a dip below the line in the middle
                is not counted in Weeks. The red dashed lines on the chart mark the same thresholds.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
